import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { Button, DateInput, Select } from '../../components/ui';
import { SearchIcon, PrinterIcon } from '../../components/ui/Icons';
import { formatDate as formatDateGlobal } from '../../config/appConfig';
import { fetcher } from '../../services/fetcher';
import {
  COMPLIANCE_CODE_META,
  COMPLIANCE_META_BY_CODE,
  fmtComplianceHours,
  type ComplianceCodeMeta,
  type WrhRulesThresholds,
} from './complianceCodes';

const todayIso = () => new Date().toISOString().slice(0, 10);

const monthStartIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

type ComplianceRow = {
  wrhDayId: number;
  workDate: string;
  crewName: string;
  rankName?: string | null;
  workHours: number;
  restHours: number;
  violationCodes: string[];
  vesselName?: string;
};

type ComplianceResponse = {
  rows: ComplianceRow[];
  periodFrom: string;
  periodTo: string;
  vesselId?: number;
  vesselName?: string;
};

const toneChip: Record<ComplianceCodeMeta['tone'], string> = {
  rose: 'border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-300',
  amber: 'border-amber-500/30 bg-amber-500/10 text-amber-800 dark:text-amber-300',
  orange: 'border-orange-500/30 bg-orange-500/10 text-orange-800 dark:text-orange-300',
};

const CodeChip: React.FC<{ code: string; rules?: WrhRulesThresholds | null }> = ({ code, rules }) => {
  const meta = COMPLIANCE_META_BY_CODE[code];
  const label = meta?.short ?? code;
  const title = meta ? `${meta.title}\n${meta.explain(rules)}` : code;
  return (
    <span
      title={title}
      className={cn(
        'inline-flex items-center rounded-md border px-1.5 py-0.5 text-[11px] font-semibold',
        meta ? toneChip[meta.tone] : 'border-zinc-500/30 bg-zinc-500/10 text-zinc-700 dark:text-zinc-300',
      )}
    >
      {label}
    </span>
  );
};

const CompliancePage: React.FC<{ lockedVesselId?: number }> = ({ lockedVesselId }) => {
  const { colors } = useTheme();
  const [params] = useSearchParams();
  const { data: lookups } = useSWR<{ vessels: Array<{ id: number; vesselName: string }> }>(
    '/crew/lookups',
    fetcher,
    { revalidateOnFocus: false },
  );
  const { data: ruleSets } = useSWR<WrhRulesThresholds[]>('/wrh/rules', fetcher, { revalidateOnFocus: false });
  const activeRules = ruleSets?.find((r) => r.isActive) ?? ruleSets?.[0] ?? null;

  const initialVessel =
    lockedVesselId != null
      ? String(lockedVesselId)
      : params.get('vesselId')?.trim() || '';
  const initialFrom = params.get('periodFrom')?.trim() || monthStartIso();
  const initialTo = params.get('periodTo')?.trim() || todayIso();

  const [vesselId, setVesselId] = useState(initialVessel);
  const [periodFrom, setPeriodFrom] = useState(initialFrom);
  const [periodTo, setPeriodTo] = useState(initialTo);
  const [applied, setApplied] = useState<{ vesselId: string; periodFrom: string; periodTo: string } | null>(
    initialVessel ? { vesselId: initialVessel, periodFrom: initialFrom, periodTo: initialTo } : null,
  );

  const vesselOptions = useMemo(
    () => (lookups?.vessels ?? []).map((v) => ({ value: String(v.id), label: v.vesselName })),
    [lookups?.vessels],
  );

  const key =
    applied &&
    `/wrh/compliance?vesselId=${applied.vesselId}&periodFrom=${applied.periodFrom}&periodTo=${applied.periodTo}`;
  const { data, isLoading } = useSWR<ComplianceResponse>(key, fetcher, { revalidateOnFocus: false });

  const rows = data?.rows ?? [];
  const vesselLabel =
    data?.vesselName ||
    lookups?.vessels?.find((v) => String(v.id) === applied?.vesselId)?.vesselName ||
    null;

  const codeCounts = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of rows) {
      for (const c of r.violationCodes ?? []) {
        map.set(c, (map.get(c) ?? 0) + 1);
      }
    }
    return COMPLIANCE_CODE_META.map((m) => ({ ...m, count: map.get(m.code) ?? 0 })).filter((m) => m.count > 0);
  }, [rows]);

  const crewCount = useMemo(() => new Set(rows.map((r) => r.crewName)).size, [rows]);

  const printHref =
    applied &&
    `/wrh/compliance/report?vesselId=${encodeURIComponent(applied.vesselId)}&periodFrom=${encodeURIComponent(applied.periodFrom)}&periodTo=${encodeURIComponent(applied.periodTo)}`;

  return (
    <div className={cn('flex h-full min-h-0 flex-col gap-2', colors.background)}>
      <div
        className={cn(
          'flex shrink-0 flex-wrap items-center gap-2 rounded-xl border px-3 py-2',
          colors.border,
          colors.surface,
        )}
      >
        {!lockedVesselId && (
          <div className="w-[11rem]">
            <Select
              value={vesselId}
              onChange={(e) => setVesselId(e.target.value)}
              options={vesselOptions}
              placeholder="Vessel"
              showClearButton={false}
            />
          </div>
        )}
        <div className="w-[9.5rem]">
          <DateInput
            value={periodFrom}
            onChange={(e) => setPeriodFrom(e.target.value || monthStartIso())}
            aria-label="From"
          />
        </div>
        <span className={cn('text-xs', colors.textMuted)}>to</span>
        <div className="w-[9.5rem]">
          <DateInput
            value={periodTo}
            onChange={(e) => setPeriodTo(e.target.value || todayIso())}
            aria-label="To"
          />
        </div>
        <Button
          type="button"
          size="sm"
          onClick={() => {
            const vid = lockedVesselId ? String(lockedVesselId) : vesselId;
            if (!vid) return;
            setApplied({ vesselId: vid, periodFrom, periodTo });
          }}
        >
          <SearchIcon className="h-4 w-4" />
          Search
        </Button>

        {applied && (
          <>
            <div className={cn('mx-1 hidden h-6 w-px sm:block', 'bg-black/10 dark:bg-white/10')} />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                <span className={cn('truncate text-sm font-semibold', colors.text)}>
                  {vesselLabel ?? 'Compliance'}
                </span>
                <span className={cn('truncate text-xs', colors.textMuted)}>
                  {formatDateGlobal(applied.periodFrom)} – {formatDateGlobal(applied.periodTo)}
                  {data ? ` · ${rows.length} NC` : ''}
                  {crewCount > 0 ? ` · ${crewCount} crew` : ''}
                </span>
              </div>
            </div>
          </>
        )}

        {printHref ? (
          <Link to={printHref} className={cn('shrink-0', !applied && 'ml-auto')}>
            <Button type="button" size="sm" variant="secondary" disabled={isLoading}>
              <PrinterIcon className="h-4 w-4" />
              Print
            </Button>
          </Link>
        ) : null}
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-2 lg:flex-row">
        <aside
          className={cn(
            'flex w-full shrink-0 flex-col overflow-hidden rounded-xl border lg:w-72',
            colors.border,
            colors.surface,
          )}
        >
          <div
            className={cn(
              'flex shrink-0 items-center justify-between border-b px-3 py-2',
              colors.border,
            )}
          >
            <span
              className={cn(
                'text-[10px] font-semibold uppercase tracking-wide',
                colors.textSecondary,
              )}
            >
              Code legend
            </span>
            {activeRules && (
              <span className={cn('truncate text-[10px]', colors.textMuted)} title={activeRules.name}>
                {activeRules.name}
              </span>
            )}
          </div>
          <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-2.5">
            {COMPLIANCE_CODE_META.map((m) => (
              <div
                key={m.code}
                className={cn('rounded-lg border px-2.5 py-2', colors.border)}
              >
                <div className="flex items-start justify-between gap-2">
                  <span className={cn('rounded-md border px-1.5 py-0.5 text-[11px] font-semibold', toneChip[m.tone])}>
                    {m.short}
                  </span>
                  <span className={cn('font-mono text-[10px]', colors.textMuted)}>{m.code}</span>
                </div>
                <div className={cn('mt-1 text-[12px] font-medium leading-snug', colors.text)}>
                  {m.title}
                </div>
                <p className={cn('mt-0.5 text-[11px] leading-snug', colors.textMuted)}>
                  {m.explain(activeRules)}
                </p>
              </div>
            ))}
          </div>
          <div
            className={cn(
              'shrink-0 border-t px-3 py-2 text-[10px] leading-snug',
              colors.border,
              colors.textMuted,
            )}
          >
            Non-conformities only. Hover a code chip in the table for the same explanation.
          </div>
        </aside>

        <div
          className={cn(
            'flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-xl border',
            colors.border,
            colors.surface,
          )}
        >
          {!applied && !isLoading && (
            <div className={cn('flex flex-1 items-center justify-center text-sm', colors.textMuted)}>
              Choose vessel and period, then Search.
            </div>
          )}

          {isLoading && (
            <div className={cn('flex flex-1 items-center justify-center text-sm', colors.textMuted)}>
              Loading non-conformities…
            </div>
          )}

          {data && !isLoading && (
            <>
              {codeCounts.length > 0 && (
                <div
                  className={cn(
                    'flex shrink-0 flex-wrap items-center gap-1.5 border-b px-3 py-2',
                    colors.border,
                  )}
                >
                  <span
                    className={cn(
                      'mr-1 text-[10px] font-semibold uppercase tracking-wide',
                      colors.textSecondary,
                    )}
                  >
                    Breakdown
                  </span>
                  {codeCounts.map((m) => (
                    <span
                      key={m.code}
                      className={cn(
                        'inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] font-medium',
                        toneChip[m.tone],
                      )}
                      title={m.explain(activeRules)}
                    >
                      {m.short}
                      <span className="tabular-nums opacity-80">{m.count}</span>
                    </span>
                  ))}
                </div>
              )}

              <div className="min-h-0 flex-1 overflow-auto">
                {rows.length === 0 ? (
                  <div className="flex h-full flex-col items-center justify-center gap-1 px-4 py-12 text-center">
                    <span className="text-sm font-medium text-emerald-700 dark:text-emerald-400">
                      No non-conformities
                    </span>
                    <span className={cn('max-w-sm text-xs', colors.textMuted)}>
                      All recorded crew-days in this period meet the active WRH rules.
                    </span>
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr
                        className={cn(
                          'sticky top-0 z-10 border-b text-left text-[10px] font-semibold uppercase tracking-wide',
                          colors.border,
                          colors.textSecondary,
                          'bg-white/95 backdrop-blur-sm dark:bg-zinc-950/95',
                        )}
                      >
                        <th className="px-3 py-2">Date</th>
                        <th className="px-3 py-2">Crew</th>
                        <th className="hidden px-3 py-2 sm:table-cell">Rank</th>
                        <th className="px-3 py-2 text-right">Work</th>
                        <th className="px-3 py-2 text-right">Rest</th>
                        <th className="px-3 py-2">Issues</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, idx) => (
                        <tr
                          key={`${r.wrhDayId}-${r.crewName}-${r.workDate}`}
                          className={cn(
                            'border-b border-black/[0.04] dark:border-white/[0.04]',
                            idx % 2 === 1 && 'bg-black/[0.015] dark:bg-white/[0.02]',
                          )}
                        >
                          <td className="px-3 py-2 whitespace-nowrap">
                            <Link
                              to="/wrh/sheet"
                              className="font-medium text-sky-700 hover:underline dark:text-sky-400"
                            >
                              {formatDateGlobal(r.workDate)}
                            </Link>
                          </td>
                          <td className={cn('px-3 py-2 font-medium', colors.text)}>{r.crewName}</td>
                          <td className={cn('hidden px-3 py-2 sm:table-cell', colors.textMuted)}>
                            {r.rankName || '—'}
                          </td>
                          <td className={cn('px-3 py-2 text-right tabular-nums', colors.text)}>
                            {fmtComplianceHours(r.workHours)}h
                          </td>
                          <td className={cn('px-3 py-2 text-right tabular-nums', colors.text)}>
                            {fmtComplianceHours(r.restHours)}h
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex flex-wrap gap-1">
                              {(r.violationCodes ?? []).map((c) => (
                                <CodeChip key={c} code={c} rules={activeRules} />
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompliancePage;
